<template>
    <div :data-uuid="settings.uuid" :id="settings.uuid" :style="timerStyle" class="widget-timer">
        <template v-for="(value, name) in countdown">
            <CircledGraph
                :key="name + 'graph'"
                v-if="general.timerStyle === 'round' && !didUpdate"
                :stroke="general.accent"
                :diameter="general.width.value || 30"
                :stroke-width="3"
                need-graph
                :value="formatTime(value) > limits[name] ? 100 : (formatTime(value) * 100) / limits[name]"
            >
                <div :style="countDownStyle">
                    <div :style="{ fontSize: `${general.font.size}px`, lineHeight: '130%' }">
                        {{ formatTime(value) }}
                    </div>
                    <div v-if="general.displayLabels" :style="{ fontSize: `${general.font.size / 2}px` }">
                        {{ translate(name) }}
                    </div>
                </div>
            </CircledGraph>
            <div v-else :key="name" :style="countDownStyle">
                <div :style="{ fontSize: `${general.font.size}px`, lineHeight: '130%' }">
                    {{ formatTime(value) }}
                </div>
                <div v-if="general.displayLabels" :style="{ fontSize: `${general.font.size / 2}px` }">
                    {{ translate(name) }}
                </div>
            </div>
            <div
                v-if="name !== 'seconds'"
                :key="name + 'delimiter'"
                :style="delimiterStyle"
                class="timer-delimiter"
            ></div>
        </template>
    </div>
</template>

<script>
import widgetLangs from "../langs/index"
import { resolveFont, resolveMargin, resolvePadding, toPx } from "../utils/style"
import ViewMixin from "../utils/view-mixin"
import { DateTime, Interval } from "luxon"
import CircledGraph from "./CircledGraph.vue"

export default {
    components: { CircledGraph },
    props: {
        settings: {
            type: Object,
            default: () => {}
        }
    },
    mixins: [ViewMixin],
    name: "WidgetTimer",
    data() {
        return {
            interval: null,
            now: "",
            didUpdate: false,
            tick: 0,
            initial: ""
        }
    },
    created() {
        this.now = DateTime.now()

        const createHash = () =>
            btoa(
                JSON.stringify({
                    days: this.general?.days || 0,
                    hours: this.general?.hours || 0,
                    minutes: this.general?.minutes || 0,
                    seconds: this.general?.seconds || 0
                })
            )

        const setInitialDate = () => {
            this.initial = DateTime.now()
            localStorage.setItem(this.settings.uuid, DateTime.now())
            localStorage.setItem(`${this.settings.uuid}-hash`, createHash())
        }

        if (this.isCalendarType()) {
            this.initial = DateTime.now()
        } else if (!window.builder_dev_mode) {
            if (
                localStorage.getItem(this.settings.uuid) &&
                localStorage.getItem(`${this.settings.uuid}-hash`) === createHash()
            ) {
                this.initial = DateTime.fromISO(localStorage.getItem(this.settings.uuid))

                const later = this.initial.plus({
                    days: this.general.days || 0,
                    hours: this.general.hours || 0,
                    minutes: this.general.minutes || 0,
                    seconds: this.general.seconds || 0
                })

                const i = Interval.fromDateTimes(this.now, later)

                if (i.invalid) {
                    setInitialDate()
                }
            } else {
                setInitialDate()
            }
        } else {
            this.initial = DateTime.now()
        }

        this.interval = setInterval(() => {
            this.tick++
        }, 1000)
    },
    methods: {
        isCalendarType() {
            return !this.general.type_id || this.general.type_id === 1
        },
        translate(key) {
            let locale = ""

            if (this.getLocale) {
                locale = this.getLocale()
            } else if (window.widgetLocale) {
                locale = window.widgetLocale
            } else {
                locale = "en"
            }

            if (widgetLangs[locale] && widgetLangs[locale][key]) {
                return widgetLangs[locale][key]
            }

            if (widgetLangs["en"][key]) {
                return widgetLangs["en"][key]
            }

            return key
        },
        formatTime(value) {
            if (value > 0) {
                return 0
            }
            return parseInt(value) * -1
        },
        forseUpdate() {
            this.didUpdate = true
            this.$nextTick(() => {
                this.didUpdate = false
            })
        }
    },
    computed: {
        limits() {
            return {
                days: 365,
                hours: 24,
                minutes: 60,
                seconds: 60
            }
        },
        countdown() {
            const date = function () {
                if (this.isCalendarType()) {
                    const [year, month, day] = this.general.date.split("-")
                    const [hour, minute] = this.general.time.split(":")

                    let zone = this.general.timezone?.name || "Europe/Kyiv"

                    if (zone === "Europe/Kiev") {
                        zone = "Europe/Kyiv"
                    }

                    return DateTime.fromObject({ year, month, day, hour, minute }, { zone })
                } else {
                    if (!this.initial) {
                        return
                    }
                    return this.initial.plus({
                        days: this.general.days || 0,
                        hours: this.general.hours || 0,
                        minutes: this.general.minutes || 0,
                        seconds: this.general.seconds || 0
                    })
                }
            }.call(this)

            const formats = ["minutes", "seconds"]

            if (this.settings.general.displayHours) {
                formats.unshift("hours")
            }

            if (this.settings.general.displayDays) {
                formats.unshift("days")
            }

            return this.now.diff(date, formats).values
        },
        desktop() {
            return this.settings.desktop
        },
        mobile() {
            return this.settings.mobile
        },
        general() {
            return this.settings.general
        },
        timerStyle() {
            const style = {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...this.resolveStyle(
                    {
                        ...resolvePadding(this.desktop.padding),
                        ...resolveMargin(this.desktop.margin)
                    },
                    {
                        ...resolvePadding(this.mobile.padding),
                        ...resolveMargin(this.mobile.margin)
                    }
                )
            }

            const type = this.general.timerStyle

            if (type === "pill") {
                style.background = this.general.accent
                style.borderRadius = toPx(100)
            }

            return style
        },
        delimiterStyle() {
            const style = {
                width: toPx(1),
                marginLeft: toPx(8),
                marginRight: toPx(8),
                height: toPx(this.general.height.value / 2)
            }

            const type = this.general.timerStyle

            if (type === "plain") {
                style.background = this.general.accent
            }

            return style
        },
        countDownStyle() {
            const style = {
                ...resolveFont(this.general.font),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                color: this.general.color
            }

            const type = this.general.timerStyle

            if (type !== "round") {
                style.width = toPx(this.general.width.value)
                style.height = toPx(this.general.height.value)
            }

            if (type === "square") {
                style.background = this.general.accent
            }

            return style
        }
    },
    watch: {
        tick() {
            this.now = DateTime.now()
        },
        "general.width.value"() {
            this.forseUpdate()
        }
    },
    destroyed() {
        if (this.interval) {
            clearInterval(this.interval)
        }
    }
}
</script>
